import { reactive } from "vue"

import reportError from "@/utils/reportError"

const MONITORED_RESOURCE_PATTERNS = [
  /(?:^|\.)sentry\.io/i,
  /ingest(?:\.[^.]+)?\.sentry\.io/i,
  /maps\.googleapis\.com/i,
  /maps\.gstatic\.com/i,
  /(?:^|\.)googleapis\.com/i,
  /(?:^|\.)gstatic\.com/i,
  /(?:^|\.)firebase(?:app)?\.com/i,
  /(?:^|\.)firebaseio\.com/i,
  /(?:^|\.)firebasestorage\.googleapis\.com/i
]
const BLOCKED_MESSAGE_PATTERNS = [/ERR_BLOCKED_BY_CLIENT/i, /blocked by client/i]
const BAIT_CLASS_NAME =
  "adsbox ad-banner ad-slot ad-placement ad-unit advert advertisement text-ad text-ads sponsored-content"
export const AD_BLOCK_DETECTION_DISMISSED_STORAGE_KEY = "station.adBlockDetection.dismissed"
const AD_BLOCK_DETECTION_DISMISSED_STORAGE_VALUE = "true"

const getLocalStorage = (context) => {
  if (typeof window === "undefined") return null

  try {
    return window.localStorage
  } catch (error) {
    reportError(error, { context })
    return null
  }
}

const readAdBlockDetectionDismissed = () => {
  const storage = getLocalStorage("adBlockDetection.readDismissal")
  if (!storage) return false

  try {
    return storage.getItem(AD_BLOCK_DETECTION_DISMISSED_STORAGE_KEY) === AD_BLOCK_DETECTION_DISMISSED_STORAGE_VALUE
  } catch (error) {
    reportError(error, { context: "adBlockDetection.readDismissal" })
    return false
  }
}

const writeAdBlockDetectionDismissed = (dismissed) => {
  const storage = getLocalStorage("adBlockDetection.writeDismissal")
  if (!storage) return

  try {
    if (dismissed) {
      storage.setItem(AD_BLOCK_DETECTION_DISMISSED_STORAGE_KEY, AD_BLOCK_DETECTION_DISMISSED_STORAGE_VALUE)
    } else {
      storage.removeItem(AD_BLOCK_DETECTION_DISMISSED_STORAGE_KEY)
    }
  } catch (error) {
    reportError(error, { context: "adBlockDetection.writeDismissal" })
  }
}

export const adBlockDetectionState = reactive({
  detected: false,
  source: null,
  evidence: null,
  dismissed: readAdBlockDetectionDismissed()
})

let listenersInstalled = false

const safeString = (value) => {
  if (!value) return null
  if (typeof value === "string") return value
  if (value instanceof Error) return value.message || value.stack || String(value)
  return String(value)
}

const addCandidate = (values, value) => {
  const normalizedValue = safeString(value)
  if (normalizedValue) {
    values.push(normalizedValue)
  }
}

const extractSignalCandidates = (signal) => {
  const candidates = []
  if (!signal) return candidates

  addCandidate(candidates, signal.message)
  addCandidate(candidates, signal.filename)
  addCandidate(candidates, signal.stack)
  addCandidate(candidates, signal.reason?.message)
  addCandidate(candidates, signal.reason?.stack)
  addCandidate(candidates, signal.error?.message)
  addCandidate(candidates, signal.error?.stack)
  addCandidate(candidates, signal.target?.src)
  addCandidate(candidates, signal.target?.href)
  addCandidate(candidates, signal.currentTarget?.src)
  addCandidate(candidates, signal.currentTarget?.href)

  return candidates
}

export const isMonitoredBlockedResourceUrl = (value) => {
  if (!value || typeof value !== "string") return false

  try {
    const baseOrigin = typeof window !== "undefined" ? window.location.origin : "https://telarya.invalid"
    const { hostname, href } = new URL(value, baseOrigin)
    return MONITORED_RESOURCE_PATTERNS.some((pattern) => pattern.test(hostname) || pattern.test(href))
  } catch {
    return MONITORED_RESOURCE_PATTERNS.some((pattern) => pattern.test(value))
  }
}

const hasBlockedByClientMessage = (signal) => {
  const candidates = extractSignalCandidates(signal)
  return candidates.some((candidate) => BLOCKED_MESSAGE_PATTERNS.some((pattern) => pattern.test(candidate)))
}

const isResourceLoadFailureForMonitoredService = (signal) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false

  const resourceUrl =
    signal?.target?.src || signal?.target?.href || signal?.currentTarget?.src || signal?.currentTarget?.href

  if (!resourceUrl || !isMonitoredBlockedResourceUrl(resourceUrl)) {
    return false
  }

  return signal?.type === "error"
}

export const isLikelyAdBlockSignal = (signal) => {
  if (!signal) return false
  if (hasBlockedByClientMessage(signal)) return true
  return isResourceLoadFailureForMonitoredService(signal)
}

export const didAdBlockHideBait = (bait, computedStyle) => {
  if (!bait) return false
  const style = computedStyle || { display: "", visibility: "", opacity: "" }
  return (
    bait.offsetHeight === 0 ||
    bait.offsetWidth === 0 ||
    bait.clientHeight === 0 ||
    bait.clientWidth === 0 ||
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.opacity === "0"
  )
}

export const markAdBlockDetected = (source, evidence = null) => {
  if (adBlockDetectionState.detected || adBlockDetectionState.dismissed) return

  adBlockDetectionState.detected = true
  adBlockDetectionState.source = source
  adBlockDetectionState.evidence = safeString(evidence)

  console.warn("Ad blocker detected", {
    source,
    evidence: adBlockDetectionState.evidence
  })
}

export const dismissAdBlockDetection = () => {
  adBlockDetectionState.detected = false
  adBlockDetectionState.source = null
  adBlockDetectionState.evidence = null
  adBlockDetectionState.dismissed = true

  writeAdBlockDetectionDismissed(true)
}

const runAdBlockBaitCheck = () => {
  if (typeof document === "undefined" || !document.body || adBlockDetectionState.detected) return

  const bait = document.createElement("div")
  bait.className = BAIT_CLASS_NAME
  bait.setAttribute("aria-hidden", "true")
  bait.style.cssText = [
    "position:absolute",
    "pointer-events:none",
    "height:1px",
    "width:1px",
    "left:-10000px",
    "top:-10000px",
    "opacity:1"
  ].join(";")

  document.body.appendChild(bait)

  window.requestAnimationFrame(() => {
    try {
      const computedStyle = window.getComputedStyle(bait)
      if (didAdBlockHideBait(bait, computedStyle)) {
        markAdBlockDetected("bait-check", "adblock-bait-hidden")
      }
    } catch (error) {
      reportError(error, { context: "adBlockDetection.baitCheck" })
    } finally {
      bait.remove()
    }
  })
}

export const handlePotentialAdBlockSignal = (signal, source = "unknown") => {
  if (isLikelyAdBlockSignal(signal)) {
    markAdBlockDetected(source, signal?.message || signal?.target?.src || signal?.reason?.message || signal)
    return true
  }
  return false
}

export const installAdBlockDetection = () => {
  if (listenersInstalled || typeof window === "undefined") return
  listenersInstalled = true

  window.addEventListener("storage", (event) => {
    if (event.key !== AD_BLOCK_DETECTION_DISMISSED_STORAGE_KEY) return

    adBlockDetectionState.dismissed = event.newValue === AD_BLOCK_DETECTION_DISMISSED_STORAGE_VALUE
    if (adBlockDetectionState.dismissed) {
      adBlockDetectionState.detected = false
      adBlockDetectionState.source = null
      adBlockDetectionState.evidence = null
    }
  })

  window.addEventListener(
    "error",
    (event) => {
      handlePotentialAdBlockSignal(event, "window.error")
    },
    true
  )
  window.addEventListener("unhandledrejection", (event) => {
    handlePotentialAdBlockSignal(event?.reason ?? event, "window.unhandledrejection")
  })

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runAdBlockBaitCheck, { once: true })
  } else {
    runAdBlockBaitCheck()
  }
}

export const resetAdBlockDetectionForTests = () => {
  adBlockDetectionState.detected = false
  adBlockDetectionState.source = null
  adBlockDetectionState.evidence = null
  adBlockDetectionState.dismissed = false
  writeAdBlockDetectionDismissed(false)
}
